import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'
import { CREATE_PRODUCT } from '../graphql/product-mutations'
import { GET_PRODUCTS, GET_PRODUCT_STATS } from '../graphql/product-queries'
import { GET_PLATFORMS } from '../graphql/platform-queries'
import { CreateProductData, Platform, PlatformAvailability } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import PlatformPicker from '../components/PlatformPicker'
import toast from 'react-hot-toast'

const CreateProductPage = () => {
  const navigate = useNavigate()
  // removed unused client
  const [formData, setFormData] = useState<CreateProductData>({
    name: '',
    kind: 'BASE',
    description: '',
    image_url: '',
    active: true,
    display_in_pos: true,
    display_in_webshop: true,
  })
  const [enabledSlugs, setEnabledSlugs] = useState<Set<string>>(new Set())

  const { data: platformsData, loading: platformsLoading } = useQuery<{ platforms: Platform[] }>(
    GET_PLATFORMS,
    { variables: { activeOnly: true } }
  )
  const platforms = platformsData?.platforms || []
  const availabilities: PlatformAvailability[] = platforms.map(p => ({
    platform: p,
    enabled: enabledSlugs.has(p.slug),
  }))

  const handlePlatformChange = (slug: string, enabled: boolean) => {
    setEnabledSlugs(prev => {
      const next = new Set(prev)
      if (enabled) next.add(slug)
      else next.delete(slug)
      return next
    })
  }

  const [createProduct, { loading }] = useMutation(CREATE_PRODUCT, {
    onCompleted: (data) => {
      toast.success('Product created successfully!')
      navigate(`/products/${data.createProduct.id}`)
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create product')
    },
    update: (cache, { data }) => {
      if (data?.createProduct) {
        // Update the products list cache
        const existingProducts: any = cache.readQuery({
          query: GET_PRODUCTS,
          variables: { filters: {} }
        })

        if (existingProducts) {
          cache.writeQuery({
            query: GET_PRODUCTS,
            variables: { filters: {} },
            data: {
              products: [...existingProducts.products, data.createProduct]
            }
          })
        } else {
        }

        // Update product stats
        const existingStats: any = cache.readQuery({
          query: GET_PRODUCT_STATS
        })

        if (existingStats) {
          cache.writeQuery({
            query: GET_PRODUCT_STATS,
            data: {
              productStats: {
                ...existingStats.productStats,
                total: existingStats.productStats.total + 1,
                active: existingStats.productStats.active + (data.createProduct.active ? 1 : 0),
                inactive: existingStats.productStats.inactive + (data.createProduct.active ? 0 : 1)
              }
            }
          })
        } else {
        }
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Product name is required')
      return
    }

    createProduct({
      variables: {
        productData: {
          ...formData,
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
          image_url: formData.image_url?.trim() || undefined,
          platformSlugs: Array.from(enabledSlugs)
        }
      }
    })
  }

  const handleInputChange = (field: keyof CreateProductData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/products"
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Create New Product</h1>
          <p className="text-sm text-gray-500">Add a new product to your menu</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Product Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter product name"
                  required
                />
              </div>

              <div>
                <label htmlFor="kind" className="block text-sm font-medium text-gray-700">
                  Product Type *
                </label>
                <select
                  id="kind"
                  value={formData.kind}
                  onChange={(e) => handleInputChange('kind', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="BASE">🍽️ Base Product</option>
                  <option value="MODIFIER">➕ Modifier</option>
                  <option value="BUNDLE">📦 Bundle</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  {formData.kind === 'BASE' && 'Main menu items that customers can order'}
                  {formData.kind === 'MODIFIER' && 'Add-ons or customizations for base products'}
                  {formData.kind === 'BUNDLE' && 'Combination of multiple products sold together'}
                </p>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter product description"
                />
              </div>

              <div>
                <label htmlFor="image_url" className="block text-sm font-medium text-gray-700">
                  Image URL
                </label>
                <div className="mt-1 flex items-center space-x-3">
                  <input
                    type="url"
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => handleInputChange('image_url', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                  />
                  {formData.image_url && (
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="h-12 w-12 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Optional: URL to a product image
                </p>
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => handleInputChange('active', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_pos"
                    checked={formData.display_in_pos}
                    onChange={(e) => handleInputChange('display_in_pos', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_pos" className="ml-2 block text-sm text-gray-700">
                    Display in POS
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_webshop"
                    checked={formData.display_in_webshop}
                    onChange={(e) => handleInputChange('display_in_webshop', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_webshop" className="ml-2 block text-sm text-gray-700">
                    Display in Webshop
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Platforms */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Available on delivery platforms</h2>
            <p className="text-sm text-gray-500 mb-4">
              Only products marked here are pushed to that platform's catalog. Requires the product's category to also be enabled.
            </p>
            <PlatformPicker
              availabilities={availabilities}
              onChange={handlePlatformChange}
              disabled={platformsLoading}
            />
            {enabledSlugs.size > 0 && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                After creating, assign this product to a delivery-enabled category — otherwise it won't appear on the platform.
              </p>
            )}
          </div>

          {/* Product Type Specific Information */}
          {formData.kind === 'BUNDLE' && (
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Bundle Information</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <PhotoIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Bundle Configuration
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        After creating this bundle product, you'll be able to:
                      </p>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>Add meal groups (e.g., "Choose your main", "Select sides")</li>
                        <li>Configure required selections for each group</li>
                        <li>Set up eligible variants and price adjustments</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {formData.kind === 'MODIFIER' && (
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Modifier Information</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <PhotoIcon className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Modifier Setup
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>
                        After creating this modifier, you'll be able to:
                      </p>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>Add variants (e.g., "Small", "Medium", "Large")</li>
                        <li>Set individual prices for each variant</li>
                        <li>Assign to base products that can use this modifier</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <Link
              to="/products"
              className="btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="btn-primary"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Creating...
                </>
              ) : (
                'Create Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateProductPage
